import { useInfiniteQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useDebounceValue } from "usehooks-ts";
import { listRemoteAtsJobs } from "@/lib/api/generated";
import {
  REMOTE_JOBS_SYNC_PENDING_CAP_MS,
  REMOTE_JOBS_SYNC_PENDING_POLL_MS,
} from "../lib/ats-job-picker";
import type { AtsRemoteJob } from "../types";

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;

function toRemoteJob(j: {
  remote_id: string;
  name: string;
  status?: string | null;
  already_linked?: boolean;
  stages?: AtsRemoteJob["stages"];
  description?: string | null;
}): AtsRemoteJob {
  return {
    remote_job_id: j.remote_id,
    title: j.name,
    status: j.status ?? "",
    already_linked: j.already_linked ?? false,
    stages: j.stages ?? [],
    description: j.description ?? null,
  };
}

export function useAtsRemoteJobs(connectionId: string | null, search = "") {
  const [debouncedSearch] = useDebounceValue(search, SEARCH_DEBOUNCE_MS);
  const q = debouncedSearch.trim() || undefined;
  const startedAtRef = useRef(Date.now());
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    startedAtRef.current = Date.now();
    setTimedOut(false);
    const timeout = setTimeout(
      () => setTimedOut(true),
      REMOTE_JOBS_SYNC_PENDING_CAP_MS,
    );
    return () => clearTimeout(timeout);
  }, [connectionId, q]);

  const query = useInfiniteQuery({
    queryKey: ["ats", "remote-jobs", connectionId, q ?? ""],
    queryFn: ({ pageParam, signal }) =>
      listRemoteAtsJobs(
        connectionId!,
        {
          q,
          cursor: pageParam || undefined,
          page_size: PAGE_SIZE,
        },
        { signal },
      ),
    initialPageParam: "",
    getNextPageParam: (lastPage) => lastPage.next_cursor || undefined,
    enabled: !!connectionId,
    refetchInterval: (query) => {
      if (
        timedOut ||
        Date.now() - startedAtRef.current >= REMOTE_JOBS_SYNC_PENDING_CAP_MS
      ) {
        return false;
      }
      const pending =
        query.state.data?.pages.at(-1)?.sync_pending === true;
      if (!pending) return false;
      return REMOTE_JOBS_SYNC_PENDING_POLL_MS;
    },
  });

  const syncPending =
    query.data?.pages.at(-1)?.sync_pending === true;

  return {
    jobs: (query.data?.pages ?? []).flatMap((page) =>
      (page.jobs ?? []).map(toRemoteJob),
    ),
    isLoading: query.isLoading,
    isError: query.isError,
    syncPending,
    syncPendingTimedOut: syncPending && timedOut,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    fetchNextPage: query.fetchNextPage,
  };
}
